import { DetailedInsight } from '@api/api/insights';
import {
  EventsConfig,
  hasOHLC,
  PriceHoverData,
  RangeHorizon,
  rangeToResampleMapping,
} from '@chart';
import { ChartV2Point, RenderLegend } from '@chartv2';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ChartAreaWrapper } from '@terminal/components/chart-area-wrapper/ChartAreaWrapper';
import { ChartAreaV2 } from '@terminal/modules/simple-chart/components/chart-area-v2/ChartAreaV2';
import { useChartV2Insights } from '@terminal/views/chart-v2/hooks/useChartV2Insights';
import {
  getChartV2SeriesId,
  terminalPanesToDefaultPriceDisplayByPane,
  terminalPanesToSeries,
  toChartV2Domain,
} from '@terminal/views/chart-v2/utils/map-terminal-chart';
import { LegendItem } from '@terminal/views/turbo-chart/components/legend-item/LegendItem';
import { useChartTheme } from '@terminal/views/turbo-chart/hooks/use-chart-theme/useChartTheme';
import { useTurboChart } from '@terminal/views/turbo-chart/hooks/use-turbo-chart/useTurboChart';
import { ValidChartSettings } from '@terminal/views/turbo-chart/utils/chart-settings-utils/chart-settings-utils';
import { TerminalChartAssetData } from '@terminal/views/turbo-chart/utils/chart-utils/types';
import { useMemo } from 'react';

import styles from './ChartBody.module.scss';
import { filterByDateRange, getCommonDateRange } from './utils';

const SINGLE_PANE_HEIGHT = 180;
const TMI_EVENTS_CONFIG: EventsConfig = { TMI: true };

const getLegendHoverData = (
  chartAssetData: TerminalChartAssetData,
  seriesData: ChartV2Point[],
  point: ChartV2Point | undefined
): PriceHoverData | undefined => {
  if (!point) {
    return undefined;
  }

  const pointIndex = seriesData.indexOf(point);
  const currentPrice = chartAssetData.ts[pointIndex];

  if (!currentPrice) {
    return undefined;
  }

  return {
    chartAssetData,
    currentPrice,
    previousPrice: chartAssetData.ts[pointIndex - 1] ?? currentPrice,
    priceFormatted: currentPrice.close.toString(),
  };
};

export interface ChartBodyProps {
  assets: {
    tag: string;
    snake: string;
    paneIndex: number;
  }[];
  episodes: DetailedInsight['episodes'];
  horizon: RangeHorizon;
}

export const ChartBody = ({ assets, episodes, horizon }: ChartBodyProps) => {
  const { colors: themeColors } = useChartTheme();

  const initialChartSettings: ValidChartSettings = {
    assets,
    resample: rangeToResampleMapping[horizon],
    horizon,
    domain: null,
    hidden: undefined,
    episodes,
    paneSettings: {
      0: {
        yAxisType: 'split',
      },
    },
  };

  const {
    assetWithError,
    hasError,
    hasEmpty,
    chartState,
    primaryAsset,
    primaryPane,
    retryInitialChartLoad,
    changeRange,
    changeSeriesType,
    chartPaneActions,
  } = useTurboChart({
    initialChartSettings,
    shouldUseSearchParams: false,
    variants: ['customMulti'],
  });

  const chartData = useMemo(
    () => chartState.chartPanes.flatMap(pane => pane.chartAssetData),
    [chartState.chartPanes]
  );

  const { trimmedChartState, trimmedPrimaryAsset } = useMemo(() => {
    if (!chartData.length || !chartState.chartPanes.length) {
      return {
        trimmedChartState: chartState,
        trimmedPrimaryAsset: primaryAsset,
      };
    }

    const dateRange = getCommonDateRange(chartData);

    if (!dateRange) {
      return {
        trimmedChartState: chartState,
        trimmedPrimaryAsset: primaryAsset,
      };
    }

    if (dateRange.start > dateRange.end) {
      return {
        trimmedChartState: chartState,
        trimmedPrimaryAsset: primaryAsset,
      };
    }

    const trimmedPanes = chartState.chartPanes.map(pane => ({
      ...pane,
      chartAssetData: pane.chartAssetData.map(asset => {
        if (!asset.ts.length) {
          return asset;
        }

        const trimmedTs = filterByDateRange(
          asset.ts,
          dateRange.start,
          dateRange.end
        );
        const trimmedTsByTime = new Map<string, number>();
        trimmedTs.forEach((item, idx) => trimmedTsByTime.set(item.time, idx));

        return {
          ...asset,
          ts: trimmedTs,
          tsByTime: trimmedTsByTime,
          originalTs: filterByDateRange(
            asset.originalTs,
            dateRange.start,
            dateRange.end
          ),
        };
      }),
    }));

    const trimmedPrimary = primaryAsset
      ? (trimmedPanes
          .flatMap(p => p.chartAssetData)
          .find(asset => asset.entity.id === primaryAsset.entity.id) ??
        primaryAsset)
      : undefined;

    return {
      trimmedChartState: {
        ...chartState,
        chartPanes: trimmedPanes,
        domain: [
          dateRange.start.toISOString(),
          dateRange.end.toISOString(),
        ] as [string, string],
      },
      trimmedPrimaryAsset: trimmedPrimary,
    };
  }, [chartData, chartState, primaryAsset]);

  const { chartPanes, horizon: chartHorizon, resample } = trimmedChartState;

  const chartV2Series = useMemo(
    () =>
      terminalPanesToSeries(chartPanes, {
        shouldUseLineColorToken: true,
        showLatestPrice: false,
      }),
    [chartPanes]
  );

  const episodeBands = episodes.flatMap(episode => {
    const start = Date.parse(episode.start_date);
    const end = Date.parse(episode.end_date);

    if (!Number.isFinite(start) || !Number.isFinite(end)) {
      return [];
    }

    return [{ start, end, direction: 'positive' as const }];
  });

  const chartV2DefaultPriceDisplayByPane =
    terminalPanesToDefaultPriceDisplayByPane(chartPanes);

  const chartV2Insights = useChartV2Insights({
    chartPanes,
    eventsConfig: TMI_EVENTS_CONFIG,
    small: true,
  });

  const chartV2Domain = toChartV2Domain(
    trimmedChartState.domain,
    trimmedPrimaryAsset
  );

  const trimmedChartData = chartPanes.flatMap(pane => pane.chartAssetData);

  const renderLegend: RenderLegend = ({ entries, pointHover }) => (
    <>
      {entries.map(entry => {
        const asset = trimmedChartData.find(
          chartAsset => getChartV2SeriesId(chartAsset) === entry.series.id
        );

        if (!asset) {
          return null;
        }

        const parsedPaneIndex = Number.parseInt(
          (entry.series.paneId ?? 'pane-0').replace(/^pane-/, ''),
          10
        );
        const paneIndex = Number.isFinite(parsedPaneIndex)
          ? parsedPaneIndex
          : 0;
        const hoverPoint = pointHover?.hits.find(
          hit => hit.seriesId === entry.series.id
        )?.point;

        return (
          <LegendItem
            key={entry.series.id}
            chartAssetData={asset}
            changeChartLineColor={() => {}}
            changeSeriesType={changeSeriesType}
            chartPaneActions={chartPaneActions}
            colorArray={[]}
            hasOHLC={hasOHLC(asset)}
            lastPaneIndex={chartPanes.length - 1}
            multiplePlotted={entries.length > 1}
            onHideAsset={() => {}}
            onRemoveItem={() => {}}
            onChangeIndicator={() => {}}
            paneIndex={paneIndex}
            selectedSeriesType={asset.seriesType}
            hoverData={getLegendHoverData(asset, entry.series.data, hoverPoint)}
            legendLogoSize={20}
            hasHover={false}
            renderLogo={() => {
              return null;
            }}
          />
        );
      })}
    </>
  );

  const isLoading = !hasError && !hasEmpty && !chartData[0];

  if (isLoading) {
    return (
      <SkeletonLoader
        areas={[
          {
            styles: {
              width: '100%',
              height: '180px',
              borderRadius: '8px',
            },
          },
        ]}
      />
    );
  }

  if (!trimmedPrimaryAsset || !primaryPane) {
    return null;
  }

  return (
    <ChartAreaWrapper
      className={styles.chartWrapper}
      rootStyle={{ height: assets.length * SINGLE_PANE_HEIGHT }}
      assetWithError={assetWithError}
      hasError={hasError}
      hasEmpty={hasEmpty}
      colors={themeColors}
      chartData={chartData}
      onRetry={retryInitialChartLoad}
      renderChartArea={() => (
        <ChartAreaV2
          series={chartV2Series}
          chartData={trimmedChartData}
          primaryPane={primaryPane}
          horizon={chartHorizon}
          resample={resample}
          availableHorizons={[]}
          disable1DHorizon={false}
          insights={chartV2Insights}
          enableZoom={false}
          enableHover={false}
          usePaneHeightForYAxisTickCount
          onHorizonChange={changeRange}
          changeSeriesType={changeSeriesType}
          domain={chartV2Domain}
          defaultPriceDisplayByPane={chartV2DefaultPriceDisplayByPane}
          theme={{
            thresholdPositive: `color-mix(in srgb, ${themeColors.primary.value} 40%, transparent)`,
          }}
          showPriceDisplayToggle={false}
          thresholdBands={episodeBands}
          renderLegend={renderLegend}
        />
      )}
    />
  );
};
